import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import BespokeEnquiryModal from './BespokeEnquiryModal';
import { FetchSubAdminsDetails } from '../features/subAdmin/SubAdminSlice';

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    zIndex: 2147483647,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px'
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    maxWidth: '500px',
    width: '100%',
    padding: '40px',
    textAlign: 'center'
  },
  icon: {
    fontSize: '64px',
    color: '#22c55e',
    marginBottom: '16px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '12px'
  },
  description: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '32px',
    lineHeight: '1.5'
  },
  button: {
    padding: '14px 28px',
    borderRadius: '12px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
    width: '100%',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  primaryButton: {
    background: 'linear-gradient(to right, #2563eb, #4f46e5)',
    color: '#ffffff',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
  },
  secondaryButton: {
    background: '#f9fafb',
    color: '#111827',
    border: '2px solid #e5e7eb'
  },
  tertiaryButton: {
    background: 'linear-gradient(to right, #7c3aed, #8b5cf6)',
    color: '#ffffff',
    border: 'none'
  }
};

const OnboardingCompleteModal = ({ isOpen, onClose, onStartTrial, onPurchaseSubscription, isInTrial = false, trialDaysLeft = 0, isTrialExpired = false, companyId, hasBespokeEnquiry = false }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showBespokeEnquiry, setShowBespokeEnquiry] = useState(false);
  const [bespokeEnquirySent, setBespokeEnquirySent] = useState(hasBespokeEnquiry);

  useEffect(() => {
    setBespokeEnquirySent(hasBespokeEnquiry);
  }, [hasBespokeEnquiry]);

  const handleStartTrial = async () => {
    setLoading(true);
    try {
      await onStartTrial();
      toast.success('Trial started successfully! You have 14 days of free access.');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to start trial. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSubscription = () => {
    onClose();
    onPurchaseSubscription();
  };

  if (!isOpen) return null;

  return (
    <>
      {ReactDOM.createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={styles.backdrop}
              onClick={onClose}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                style={styles.modal}
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                >
                  <i className="fa fa-check-circle" style={styles.icon}></i>
                </motion.div>
                
                <motion.h2
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  style={styles.title}
                >
                  {isInTrial ? 'Trial Active' : isTrialExpired ? 'Trial Expired' : 'Setup Complete!'}
                </motion.h2>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  style={styles.description}
                >
                  {isInTrial 
                    ? `You have ${trialDaysLeft} days remaining in your trial period.`
                    : isTrialExpired
                    ? 'Your trial period has expired. Please purchase a subscription to continue accessing all features.'
                    : 'Congratulations! You\'ve completed all the required setup steps. Choose how you\'d like to get started:'
                  }
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  {!isInTrial && !isTrialExpired && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleStartTrial}
                      disabled={loading}
                      style={{ ...styles.button, ...styles.primaryButton }}
                    >
                      {loading ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i>
                          Starting Trial...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-clock"></i>
                          Start 14-Day Free Trial
                        </>
                      )}
                    </motion.button>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handlePurchaseSubscription}
                    style={{ ...styles.button, ...styles.secondaryButton }}
                  >
                    <i className="fa fa-credit-card"></i>
                    Purchase Subscription
                  </motion.button>

                  {!bespokeEnquirySent && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowBespokeEnquiry(true)}
                      style={{ ...styles.button, ...styles.tertiaryButton }}
                    >
                      <i className="fa fa-comments"></i>
                      Enquire For Bespoke
                    </motion.button>
                  )}
                </motion.div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{ fontSize: '13px', color: '#9ca3af', marginTop: '20px' }}
                >
                  You can change your plan later at any time
                </motion.p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <BespokeEnquiryModal
        isOpen={showBespokeEnquiry}
        onClose={() => setShowBespokeEnquiry(false)}
        companyId={companyId}
        onSuccess={() => {
          setBespokeEnquirySent(true);
          setShowBespokeEnquiry(false);
          onClose();
          if (companyId) {
            dispatch(FetchSubAdminsDetails(companyId));
          }
        }}
      />
    </>
  );
};

export default OnboardingCompleteModal;
